import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import { PartTypeLiveSearchItem } from '../../../@types';
import { InteractionsPartRequestFormInitialPartType } from '../../../@types/interactions.type';
import { PartConditionOptions } from '../../../@types/part.type';
import { ServiceObject } from '../../../@types/service.type';
import { COLORS } from '../../../configs/colors';
import { PartTypeCategory } from '../../../configs/enums';
import { useGetInteractionsPartRequestFormPartTypes } from '../../../queries/interactions-query';
import { useGetPartTypesByClientContract } from '../../../queries/part-type-query';

export interface InteractionPartsRequestValue {
  partId?: number;
  partName?: string;
  serialised?: boolean;
}

interface MobilePartsRequestFieldProps {
  service: ServiceObject;
  title: string;
  isRequired: boolean;
  value: InteractionPartsRequestValue | undefined;
  onChange: (value: InteractionPartsRequestValue | undefined) => void;
}

interface SelectableItem {
  id: number;
  name: string;
  number?: string;
  serialised: boolean;
  source: 'installed' | 'contract';
}

const isContractSerialised = (item: PartTypeLiveSearchItem): boolean =>
  item.categoryCode === PartTypeCategory.Serialised;

const isInstalledSerialised = (item: InteractionsPartRequestFormInitialPartType): boolean =>
  item.serialised === 'Serialised' ||
  item.categoryCode?.toString?.() === PartTypeCategory.Serialised;

interface SelectedChipProps {
  value: InteractionPartsRequestValue;
  showPicker: boolean;
  onClear: () => void;
}

const SelectedChip: React.FC<SelectedChipProps> = ({ value, showPicker, onClear }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 12px',
      backgroundColor: COLORS.LightBlueBackground,
      border: `1px solid ${COLORS.NavyBlue}`,
      borderRadius: 1,
      marginBottom: showPicker ? 1 : 0,
    }}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Box sx={{ fontSize: 14, fontWeight: 600, color: COLORS.NavyBlue }}>{value.partName}</Box>
      <Box sx={{ fontSize: 12, color: COLORS.Gray }}>
        {value.serialised ? 'Serialised' : 'Consumable'}
      </Box>
    </Box>
    <IconButton
      size="small"
      onClick={onClear}
      aria-label={`Clear selected part type ${value.partName ?? ''}`}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  </Box>
);

interface PickerProps {
  items: SelectableItem[];
  isLoading: boolean;
  searchText: string;
  onSearchChange: (value: string) => void;
  onSelect: (item: SelectableItem) => void;
}

const Picker: React.FC<PickerProps> = ({
  items,
  isLoading,
  searchText,
  onSearchChange,
  onSelect,
}) => (
  <Box>
    <TextField
      fullWidth
      size="small"
      placeholder="Search part types..."
      value={searchText}
      onChange={(e) => onSearchChange(e.target.value)}
      aria-label="Search part types"
    />
    <Box
      sx={{
        marginTop: 1,
        maxHeight: 220,
        overflowY: 'auto',
        border: `1px solid ${COLORS.SubtleGrey}`,
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {isLoading && items.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {!isLoading && items.length === 0 && (
        <Box sx={{ textAlign: 'center', padding: 2, color: COLORS.Gray, fontSize: 14 }}>
          {searchText ? 'No matching part types' : 'No part types available'}
        </Box>
      )}
      {items.map((item) => (
        <Button
          key={`${item.source}-${item.id}`}
          onClick={() => onSelect(item)}
          sx={{
            justifyContent: 'flex-start',
            textTransform: 'none',
            borderRadius: 0,
            padding: '10px 12px',
            borderBottom: `1px solid ${COLORS.SubtleGrey}`,
            '&:last-of-type': { borderBottom: 'none' },
            color: COLORS.NavyBlue,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Box sx={{ fontSize: 14, fontWeight: 600 }}>{item.name}</Box>
            {item.number && <Box sx={{ fontSize: 12, color: COLORS.Gray }}>{item.number}</Box>}
          </Box>
        </Button>
      ))}
    </Box>
  </Box>
);

export const InteractionPartsRequestField: React.FC<MobilePartsRequestFieldProps> = ({
  service,
  title,
  isRequired,
  value,
  onChange,
}) => {
  const [searchText, setSearchText] = useState('');
  const [isPicking, setIsPicking] = useState(false);

  const installedQuery = useGetInteractionsPartRequestFormPartTypes(
    service.id,
    PartConditionOptions.INSTALLED
  );
  const contractQuery = useGetPartTypesByClientContract(service.clientId, service.contractId);

  const { refetch: refetchContract } = contractQuery;
  useEffect(() => {
    if (service.clientId && service.contractId) {
      refetchContract();
    }
  }, [refetchContract, service.clientId, service.contractId]);

  const items = useMemo<SelectableItem[]>(() => {
    const installed = (installedQuery.data ?? []).map((item) => ({
      id: item.id,
      name: item.name,
      number: item.number,
      serialised: isInstalledSerialised(item),
      source: 'installed' as const,
    }));
    const installedIds = new Set(installed.map((i) => i.id));
    const contract = (contractQuery.data ?? [])
      .filter((item) => !installedIds.has(item.id))
      .map((item) => ({
        id: item.id,
        name: item.name,
        number: item.number,
        serialised: isContractSerialised(item),
        source: 'contract' as const,
      }));

    const combined = [...installed, ...contract];
    const q = searchText.trim().toLowerCase();
    if (!q) return combined;
    return combined.filter(
      (item) => item.name?.toLowerCase().includes(q) || item.number?.toLowerCase().includes(q)
    );
  }, [installedQuery.data, contractQuery.data, searchText]);

  const handleSelect = (item: SelectableItem): void => {
    onChange({ partId: item.id, partName: item.name, serialised: item.serialised });
    setSearchText('');
    setIsPicking(false);
  };

  const handleClear = (): void => {
    // `undefined`, not `{}`: the desktop `fieldHasValue` treats any object as a value,
    // so an empty one made a cleared *required* parts field look filled and skipped
    // its validation error.
    onChange(undefined);
    setIsPicking(true);
  };

  const isLoading = installedQuery.isLoading || contractQuery.isLoading;
  const hasSelection = !!value?.partId;
  const showPicker = isPicking || !hasSelection;

  return (
    <Box>
      <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 0.5 }}>
        {title}
        {isRequired && (
          <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>
            *
          </Box>
        )}
      </Typography>

      {hasSelection && value && (
        <SelectedChip value={value} showPicker={showPicker} onClear={handleClear} />
      )}

      {showPicker && (
        <Picker
          items={items}
          isLoading={isLoading}
          searchText={searchText}
          onSearchChange={setSearchText}
          onSelect={handleSelect}
        />
      )}
    </Box>
  );
};
